// src/hooks/useCategoryAttributes.js
import { useEffect, useState } from "react";
import CommonService from "../services/common.service";

/**
 * Hook to fetch category attributes dynamically based on category_id
 * Returns attributes with their options loaded from tables, JSON files, or static data
 */
export function useCategoryAttributes(categoryId) {
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchAttributes() {
      if (!categoryId) {
        setAttributes([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        console.log(`[useCategoryAttributes] Fetching attributes for categoryId: ${categoryId}`);
        const response = await CommonService.get(`/master/category-attributes/${categoryId}`);
        
        console.log(`[useCategoryAttributes] Response for categoryId ${categoryId}:`, {
          success: response?.success,
          hasData: !!response?.data,
          attributesCount: response?.data?.attributes?.length || 0,
          attributes: response?.data?.attributes
        });
        
        if (response && response.success && response.data) {
          // Transform attributes to a more usable format
          const transformedAttributes = (response.data.attributes || []).map(attr => ({
            ...attr,
            // Create options array in react-select format
            options: (attr.options || []).map(opt => ({
              value: opt.value,
              label: opt.label || opt.value?.toString() || '',
            })),
            // Transform sub-attributes with their options
            sub_attributes: (attr.sub_attributes || []).map(subAttr => ({
              ...subAttr,
              // Create options array in react-select format for sub-attributes
              options: (subAttr.options || []).map(opt => ({
                value: opt.value,
                label: opt.label || opt.value?.toString() || '',
              })),
            })),
          }));
          
          console.log(`[useCategoryAttributes] Transformed attributes for categoryId ${categoryId}:`, {
            count: transformedAttributes.length,
            attributes: transformedAttributes.map(a => ({
              key: a.attribute_key,
              name: a.attribute_name,
              type: a.attribute_type,
              optionsCount: a.options?.length || 0,
              subAttributesCount: a.sub_attributes?.length || 0
            }))
          });
          
          setAttributes(transformedAttributes);
        } else {
          console.log(`[useCategoryAttributes] No attributes found for categoryId ${categoryId}`);
          setAttributes([]);
        }
      } catch (err) {
        console.error(`[useCategoryAttributes] Error fetching category attributes for categoryId ${categoryId}:`, err);
        setError(err);
        setAttributes([]);
      } finally {
        setLoading(false);
      }
    }
    
    fetchAttributes();
  }, [categoryId]);

  // Helper function to get attribute by key
  const getAttributeByKey = (attributeKey) => {
    return attributes.find(attr => attr.attribute_key === attributeKey);
  };

  // Helper function to get attribute options by key
  const getAttributeOptions = (attributeKey) => {
    const attribute = getAttributeByKey(attributeKey);
    return attribute?.options || [];
  };

  // Helper function to get sub-attribute by key
  const getSubAttribute = (attributeKey, subAttributeKey) => {
    const attribute = getAttributeByKey(attributeKey);
    return attribute?.sub_attributes?.find(subAttr => subAttr.sub_attribute_key === subAttributeKey);
  };

  // Helper function to get sub-attribute options
  const getSubAttributeOptions = (attributeKey, subAttributeKey) => {
    const subAttribute = getSubAttribute(attributeKey, subAttributeKey);
    return subAttribute?.options || [];
  };

  // Create a flat object with all attributes keyed by attribute_key for easy access
  const attributesByKey = attributes.reduce((acc, attr) => {
    acc[attr.attribute_key] = attr;
    return acc;
  }, {});

  return {
    attributes,
    attributesByKey,
    loading,
    error,
    getAttributeByKey,
    getAttributeOptions,
    getSubAttribute,
    getSubAttributeOptions,
  };
}


